import { motion } from 'framer-motion';
import { ArrowRight, X } from 'lucide-react';
import { useModal } from '../../hooks/modalContext';
import { toast } from 'react-hot-toast';
import { useEffect, useState } from 'react';
import { useAuth } from '../../../auth/Auth';
import { getBuildingDetailsById, getDynamicOffers, updateBuilding } from '../../../auth/_requests';
import { DynamicOffer, UpdateDueDateTypeRequest, PaymentMethodType } from '../../utils/types';
import { Spinner } from '../../UI/Spinner/Spinner';
import { useNavigate } from 'react-router-dom';
import { usePaymentOptions } from '../../hooks/PaymentOptionsContext';
import { EnumUtil } from '../../utils/enumUtils';
import { getBusinessDays } from '../../utils/paymentUtils';

/**
 * Get advance payment config based on payment method
 */
const getAdvancePaymentConfig = (
  businessDaysBeforeDueDate: number | null | undefined,
  paymentMethod: PaymentMethodType | null
): Record<string, { description: string }> => {
  const businessDays = `${getBusinessDays(businessDaysBeforeDueDate, paymentMethod)} business days`;
  return {
    threeWeekAdvance: {
      description: `Pay rent 3 weeks + ${businessDays} before the due date and earn the highest points`,
    },
    twoWeekAdvance: {
      description: `Pay rent 2 weeks + ${businessDays} before the due date and earn more points`,
    },
    oneWeekAdvance: {
      description: `Pay rent 1 week + ${businessDays} before the due date and earn points`,
    },
  };
};

const getZeroPointOption = (
  businessDaysBeforeDueDate: number | null | undefined,
  paymentMethod: PaymentMethodType | null
) => {
  const businessDays = `${getBusinessDays(businessDaysBeforeDueDate, paymentMethod)} business days`;
  return {
    points: 'Setup Autopay earlier to earn points',
    description: `Pay ${businessDays} before due date`,
    code: 'subscriptionPayment3BusinessDaysBeforeDueDate',
  };
};

const SubscriptionPoints = () => {
  const { setModal, modalState } = useModal();
  const navigate = useNavigate();

  const tenantBuildingId = modalState?.tenantBuildingId;
  const rentAmount = modalState?.rentAmount;
  const onUpdated = modalState?.onUpdated;

  const { auth } = useAuth();
  const { paymentOptions } = usePaymentOptions();

  // Get selected payment option and check if PAD
  const selectedPaymentOptionId = localStorage.getItem('selectedPaymentOption');
  const selectedPaymentOption = paymentOptions.find(
    (option) => option.id === selectedPaymentOptionId
  );
  const paymentMethod = EnumUtil.toEnum(selectedPaymentOption?.paymentMethod, PaymentMethodType);

  const [loading, setLoading] = useState<boolean>(false);
  const [pointsDescription, setPointsDescription] = useState<
    {
      points: string;
      description: string;
      code: string;
    }[]
  >([]);

  const [selectedDueDateType, setSelectedDueDateType] = useState<string>(
    'subscriptionPayment3BusinessDaysBeforeDueDate'
  );

  /**
   * Fetch dynamic offers
   */
  useEffect(() => {
    const fetchOffers = async () => {
      try {
        setLoading(true);

        const response = await getDynamicOffers(
          auth?.data.token,
          rentAmount,
          selectedPaymentOption?.paymentMethodId,
          selectedPaymentOption?.cardCompanyTypeId
        );
        const advancePaymentConfig = getAdvancePaymentConfig(
          selectedPaymentOption?.businessDaysBeforeDueDate,
          paymentMethod
        );

        const dynamicOptions = response.data.map((offer: DynamicOffer) => {
          const config = advancePaymentConfig[offer.code];
          const tickets = offer.tickets && Number(offer.tickets) > 0 ? Number(offer.tickets) : 0;
          const ticketText = tickets > 0 ? ` + ${tickets} Ticket${tickets > 1 ? 's' : ''}` : '';

          return {
            points: `Earn ${offer.dynamicPoints} TP Points${ticketText}/Month`,
            description: config?.description ?? 'Advance rent payment',
            code: offer.code,
          };
        });

        //  Keep 3-days-before-due-date option at the end
        setPointsDescription([
          ...dynamicOptions,
          getZeroPointOption(selectedPaymentOption?.businessDaysBeforeDueDate, paymentMethod),
        ]);
      } catch (error) {
        console.error('Error fetching offers:', error);
      } finally {
        setLoading(false);
      }
    };

    if (auth?.data.token && rentAmount) {
      fetchOffers();
    }
  }, [
    auth?.data.token,
    rentAmount,
    paymentMethod,
    selectedPaymentOption?.paymentMethodId,
    selectedPaymentOption?.cardCompanyTypeId,
    selectedPaymentOption?.businessDaysBeforeDueDate,
  ]);

  /**
   * Fetch building details (existing selection)
   */
  useEffect(() => {
    const fetchBuildingDetails = async () => {
      try {
        setLoading(true);

        const response = await getBuildingDetailsById(auth?.data.token, tenantBuildingId);

        if (response?.data?.length) {
          setSelectedDueDateType(
            response.data[0]?.dueDateType?.name ?? 'subscriptionPayment3BusinessDaysBeforeDueDate'
          );
        }
      } catch (error) {
        console.error('Error fetching building details:', error);
      } finally {
        setLoading(false);
      }
    };

    if (tenantBuildingId && auth?.data.token) {
      fetchBuildingDetails();
    }
  }, [auth?.data.token, tenantBuildingId]);

  /**
   * Update how many days in advance of the (fixed) due date autopay withdraws
   */
  const updateDueDateType = async () => {
    try {
      await updateBuilding(auth?.data.token, tenantBuildingId, {
        buildingDetails: {
          dueDateTypeName: selectedDueDateType,
        },
      } as UpdateDueDateTypeRequest);

      toast.success('Payment schedule updated successfully');
      if (onUpdated) {
        onUpdated();
      } else {
        navigate('/pay-now', {
          state: {
            buildingId: tenantBuildingId,
            initialStep: 1,
            payPlan: 'autopay',
            dueDateTypeSelected: true,
          },
        });
      }
      setModal(null);
    } catch (error) {
      console.error('Error updating payment schedule:', error);
      toast.error('Failed to update payment schedule');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Spinner widthFull='!bg-transparent' />;
  }

  return (
    <motion.div
      className='relative w-[554px] bg-[#FFF] rounded-[12px] flex flex-col overflow-y-auto max-h-[90vh] max-w-[90vw]'
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className='flex justify-end p-4'>
        <X
          className='cursor-pointer text-gray-500 hover:text-gray-700'
          size={24}
          onClick={() => setModal(null)}
        />
      </div>

      <div className='flex flex-col gap-4 px-10 pb-8'>
        <span className='text-main font-bold text-lg'>Game the system! 🎯</span>

        <span className='text-main text-base font-medium'>
          Pay rent with your credit card and enjoy extra days with your money before payment is
          due, plus collect TenantPay Points every month.
        </span>

        <span className='text-base text-light/80'>
          Turn on autopay and let Tenant Pay take care of rent automatically on your chosen date.
        </span>

        <span className='text-base text-light/80'>
          You pay early, but your payment is sent to your landlord on the due date.
        </span>

        <span className='mt-4 text-main text-base font-medium'>
          Select one of the options below for the next month's payment:
        </span>

        {pointsDescription.map((item, idx) => (
          <label
            key={idx}
            className={`py-4 px-3 items-center gap-4 rounded-[8px] border ${
              selectedDueDateType === item.code
                ? 'border-primary bg-primary/10'
                : 'border-[rgba(63,68,83,0.20)]'
            } flex w-full cursor-pointer`}
          >
            <input
              type='radio'
              name='subscriptionPoints'
              checked={selectedDueDateType === item.code}
              className='w-[20px] accent-primary'
              onChange={() => {
                setSelectedDueDateType(item.code);
              }}
            />

            <div className='flex flex-col gap-1 w-full ml-4'>
              <span className='text-primary text-base font-medium'>{item.points}</span>
              <span className='text-main text-sm font-semibold'>{item.description}</span>
            </div>
          </label>
        ))}

        <button
          className='submitBtn mt-10'
          onClick={() => {
            setLoading(true);
            updateDueDateType().then(() => setModal(null));
          }}
        >
          <div className='flex justify-between w-full p-4'>
            <span>Next</span>
            <ArrowRight className='text-white h-5 w-5' />
          </div>
        </button>
      </div>
    </motion.div>
  );
};

export { SubscriptionPoints };
